<template>
    <div class="breadcrumb-bar">
        <!-- Icon -->
        <div class="bc-icon-wrap">
            <i v-if="routeIcon.type === 'fa'" :class="routeIcon.value"></i>
            <feather-icon v-else :icon="routeIcon.value" svgClasses="h-5 w-5"></feather-icon>
        </div>

        <!-- Section label (category or parent page) -->
        <span class="bc-section">{{ sectionLabel }}</span>

        <!-- Arrow + current page -->
        <feather-icon icon="ChevronRightIcon" svgClasses="h-4 w-4" class="bc-arrow"></feather-icon>
        <span class="bc-page">{{ pageLabel }}</span>
    </div>
</template>

<script>
// Icons match sidebarItems.js exactly
const ROUTE_ICONS = {
    home:                   { type: 'feather', value: 'LayoutIcon' },
    // Administration
    users:                  { type: 'fa',      value: 'fas fa-users' },
    addUser:                { type: 'fa',      value: 'fas fa-users' },
    editUser:               { type: 'fa',      value: 'fas fa-users' },
    companies:              { type: 'fa',      value: 'fas fa-building' },
    addCompany:             { type: 'fa',      value: 'fas fa-building' },
    editCompany:            { type: 'fa',      value: 'fas fa-building' },
    programs:               { type: 'fa',      value: 'fas fa-city' },
    addProgram:             { type: 'fa',      value: 'fas fa-city' },
    editProgram:            { type: 'fa',      value: 'fas fa-city' },
    showProgram:            { type: 'fa',      value: 'fas fa-city' },
    prescribers:            { type: 'fa',      value: 'fas fa-user-friends' },
    editPrescriber:         { type: 'fa',      value: 'fas fa-user-friends' },
    profilePrescriber:      { type: 'fa',      value: 'fas fa-user-friends' },
    editLot:                { type: 'feather', value: 'Edit2Icon' },
    addLot:                 { type: 'feather', value: 'PlusSquareIcon' },
    programDetails:         { type: 'fa',      value: 'fas fa-city' },
    lotDetail:              { type: 'fa',      value: 'fas fa-city' },
    // CRM
    customers:              { type: 'fa',      value: 'fas fa-user' },
    editCustomer:           { type: 'fa',      value: 'fas fa-user' },
    'tracking-connections': { type: 'fa',      value: 'fas fa-chart-line' },
    denounces:              { type: 'feather', value: 'BriefcaseIcon' },
    denouncesPrescriber:    { type: 'feather', value: 'BriefcaseIcon' },
    prospects:              { type: 'feather', value: 'FileIcon' },
    prospectsTable:         { type: 'feather', value: 'FileIcon' },
    createProspect:         { type: 'feather', value: 'FileIcon' },
    showProspect:           { type: 'feather', value: 'FileIcon' },
    editProspect:           { type: 'feather', value: 'FileIcon' },
    options:                { type: 'feather', value: 'SettingsIcon' },
    optionsPrescriber:      { type: 'feather', value: 'SettingsIcon' },
    bookings:               { type: 'feather', value: 'BookmarkIcon' },
    editBookingAdmin:       { type: 'feather', value: 'BookmarkIcon' },
    bookingsPrescriber:     { type: 'feather', value: 'BookmarkIcon' },
    bookingPrescriber:      { type: 'feather', value: 'BookmarkIcon' },
    // Finances
    notifications:          { type: 'feather', value: 'BellIcon' },
    addNotification:        { type: 'feather', value: 'BellIcon' },
    editNotification:       { type: 'feather', value: 'BellIcon' },
    invoices:               { type: 'feather', value: 'DollarSignIcon' },
    'prescriber-invoices':  { type: 'feather', value: 'DollarSignIcon' },
    subscriptions:          { type: 'feather', value: 'CalendarIcon' },
    editSubscription:       { type: 'feather', value: 'CalendarIcon' },
    contrats:               { type: 'feather', value: 'FileTextIcon' },
    addContrats:            { type: 'feather', value: 'FileTextIcon' },
    articles:               { type: 'feather', value: 'ShoppingCartIcon' },
    FinanceRequest:         { type: 'feather', value: 'DollarSignIcon' },
    CreditRequest:          { type: 'feather', value: 'TrendingUpIcon' },
    // Système
    automatisation:         { type: 'feather', value: 'ActivityIcon' },
    adminTrials:            { type: 'feather', value: 'FilmIcon' },
    DataRequest:            { type: 'feather', value: 'DatabaseIcon' },
    ProductAndServices:     { type: 'feather', value: 'PackageIcon' },
    partners:               { type: 'feather', value: 'LinkIcon' },
    sponsorship:            { type: 'feather', value: 'GiftIcon' },
    outils:                 { type: 'feather', value: 'ToolIcon' },
    webinar:                { type: 'feather', value: 'VideoIcon' },
    prescriberMapSettings:  { type: 'feather', value: 'MapIcon' },
    // Prescriber home
    profile:                { type: 'feather', value: 'UserIcon' },
    homePrescriber:         { type: 'feather', value: 'HomeIcon' },
};

// Maps each route to its sidebar section header
const ROUTE_CATEGORIES = {
    // Administration
    users:                  'Administration',
    addUser:                'Administration',
    editUser:               'Administration',
    companies:              'Administration',
    addCompany:             'Administration',
    editCompany:            'Administration',
    programs:               'Administration',
    addProgram:             'Administration',
    editProgram:            'Administration',
    showProgram:            'Administration',
    prescribers:            'Administration',
    editPrescriber:         'Administration',
    profilePrescriber:      'Administration',
    editLot:                'Administration',
    addLot:                 'Administration',
    programDetails:         'Administration',
    lotDetail:              'Administration',
    // CRM
    customers:              'CRM',
    editCustomer:           'CRM',
    'tracking-connections': 'CRM',
    denounces:              'CRM',
    denouncesPrescriber:    'CRM',
    prospects:              'CRM',
    prospectsTable:         'CRM',
    createProspect:         'CRM',
    showProspect:           'CRM',
    editProspect:           'CRM',
    options:                'CRM',
    optionsPrescriber:      'CRM',
    bookings:               'CRM',
    editBookingAdmin:       'CRM',
    bookingsPrescriber:     'CRM',
    bookingPrescriber:      'CRM',
    // Finances
    notifications:          'Finances',
    addNotification:        'Finances',
    editNotification:       'Finances',
    invoices:               'Finances',
    'prescriber-invoices':  'Finances',
    subscriptions:          'Système',
    editSubscription:       'Système',
    contrats:               'Finances',
    addContrats:            'Finances',
    articles:               'Finances',
    FinanceRequest:         'Finances',
    CreditRequest:          'Finances',
    // Système
    automatisation:         'Système',
    adminTrials:            'Système',
    DataRequest:            'Système',
    ProductAndServices:     'Système',
    partners:               'Système',
    sponsorship:            'Système',
    outils:                 'Système',
    webinar:                'Système',
    prescriberMapSettings:  'Système',
};

export default {
    name: 'vx-breadcrumb',
    computed: {
        // Breadcrumb items with "Home" stripped out
        links() {
            return (this.$route.meta.breadcrumb || []).filter(
                l => l.title && l.title.toLowerCase() !== 'home'
            );
        },
        routeIcon() {
            return ROUTE_ICONS[this.$route.name] || { type: 'feather', value: 'CircleIcon' };
        },
        category() {
            return ROUTE_CATEGORIES[this.$route.name] || '';
        },
        // On a list page (1 link): show the sidebar category (Administration/CRM…)
        // On a detail page (2+ links): show the parent page title (Prescripteurs, Notifications…)
        sectionLabel() {
            if (this.links.length <= 1) {
                return this.cap(this.category);
            }
            return this.cap(this.links[0].title);
        },
        // Always the last breadcrumb item
        pageLabel() {
            const last = this.links[this.links.length - 1];
            return last ? this.cap(last.title) : '';
        },
    },
    methods: {
        cap(str) {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
    },
};
</script>

<style lang="scss" scoped>
.breadcrumb-bar {
    display: flex;
    align-items: center;
    gap: 10px;
}

.bc-icon-wrap {
    width: 40px;
    height: 40px;
    border-radius: 11px;
    background: linear-gradient(135deg, rgba(var(--vs-primary), 1) 0%, rgba(var(--vs-primary), 0.72) 100%);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 14px rgba(var(--vs-primary), 0.38);
    font-size: 17px;
}

.bc-section {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.02em;
    white-space: nowrap;
}

.bc-arrow {
    color: #cbd5e1;
    flex-shrink: 0;
}

.bc-page {
    font-size: 17px;
    font-weight: 600;
    color: rgba(var(--vs-primary), 1);
    white-space: nowrap;
}
</style>
